import React, { useEffect, useRef, useState } from "react";
import "./About.css";

function AnimatedNumber({ value, suffix }) {
  const [currentValue, setCurrentValue] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const numberRef = useRef(null);

  useEffect(() => {
    const element = numberRef.current;

    if (!element || hasStarted) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.35 }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [hasStarted]);

  useEffect(() => {
    if (!hasStarted) {
      return undefined;
    }

    const duration = 1400;
    const startTime = performance.now();

    const animate = (timestamp) => {
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easedProgress = 1 - (1 - progress) ** 3;

      setCurrentValue(Math.round(value * easedProgress));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);

    return undefined;
  }, [hasStarted, value]);

  return (
    <span ref={numberRef}>
      {currentValue}
      {suffix}
    </span>
  );
}

function About() {
  return (
    <section className="about" id="about">

      <div className="about-container">

        {/* =================================================
            LEFT CONTENT
            ================================================= */}

        <div className="about-content">

          <span className="about-eyebrow">
            ABOUT STRIDES
          </span>

          <h2>
            Driven by Energy.
            <br />
            Built for Progress.
          </h2>

          <p className="about-intro">
            Strides Energy & Maritime Ltd is committed to
            delivering reliable energy and maritime solutions
            through expertise, innovation and operational
            excellence.
          </p>

          <p className="about-description">
            We combine industry knowledge, technical
            capability and a strong commitment to safety
            to deliver solutions that create lasting value
            for our clients and partners.
          </p>

          <a
            href="/who-we-are"
            className="about-button"
          >
            <span>Discover More</span>

            <span className="about-button-arrow">
              →
            </span>
          </a>

        </div>


        {/* =================================================
            RIGHT VISUAL
            ================================================= */}

        <div className="about-visual">

          <div className="about-image-wrapper">

            <img
              src="/images/marine (1).webp"
              alt="Strides Energy & Maritime operations"
              className="about-image"
            />

            <div className="about-image-overlay"></div>

          </div>


          {/* ===============================================
              EXPERIENCE CARD
              =============================================== */}

          <div className="about-experience-card">

            <span className="about-experience-number">
              <AnimatedNumber value={20} suffix="+" />
            </span>

            <span className="about-experience-text">
              Years of
              <br />
              Experience
            </span>

          </div>

        </div>

      </div>

      {/* =================================================
          STATS
          ================================================= */}

      <div className="about-stats">

        {/* STAT 1 */}

        <div className="about-stat">

          <span className="about-stat-number">
            <AnimatedNumber value={20} suffix="+" />
          </span>

          <span className="about-stat-label">
            Years Experience
          </span>

        </div>


        {/* STAT 2 */}

        <div className="about-stat">

          <span className="about-stat-number">
            <AnimatedNumber value={500} suffix="+" />
          </span>

          <span className="about-stat-label">
            Projects Delivered
          </span>

        </div>


        {/* STAT 3 */}

        <div className="about-stat">

          <span className="about-stat-number">
            <AnimatedNumber value={24} suffix="/7" />
          </span>

          <span className="about-stat-label">
            Operational Support
          </span>

        </div>


        {/* STAT 4 */}

        <div className="about-stat">

          <span className="about-stat-number">
            <AnimatedNumber value={100} suffix="%" />
          </span>

          <span className="about-stat-label">
            Commitment
          </span>

        </div>

      </div>

    </section>
  );
}

export default About;